import env from "@/env";

const EXPLORER_ONLY_FALSE_SUBDOMAINS = new Set(["cloud", "ai"]);
const EXPLORER_ONLY_TRUE_SUBDOMAINS = new Set(["explorer", "worker"]);

const getHostname = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.location.hostname || null;
};

const getHostnameFromDomainUrl = (domainUrl: string): string | null => {
  if (!domainUrl) {
    return null;
  }

  try {
    return new URL(domainUrl).hostname;
  } catch {
    return null;
  }
};

const getExplorerOnlyModeFromProductDomain = (hostname: string | null): boolean | null => {
  if (!hostname) {
    return null;
  }

  const cloudHost = getHostnameFromDomainUrl(env.CLOUD_DOMAIN);
  const aiHost = getHostnameFromDomainUrl(env.INTELLIGENCE_DOMAIN);
  const explorerHost = getHostnameFromDomainUrl(env.EXPLORER_DOMAIN);

  const productHosts = [cloudHost, aiHost, explorerHost].filter(Boolean) as string[];
  const uniqueProductHosts = new Set(productHosts);

  // Local dev: all product domains point at the same host — defer to env/subdomain.
  if (uniqueProductHosts.size === 1 && uniqueProductHosts.has(hostname)) {
    return null;
  }

  if ((cloudHost && hostname === cloudHost) || (aiHost && hostname === aiHost)) {
    return false;
  }

  if (explorerHost && hostname === explorerHost) {
    return true;
  }

  return null;
};

const getExplorerOnlyModeFromSubdomain = (subdomain: string | null): boolean | null => {
  if (!subdomain) {
    return null;
  }

  if (EXPLORER_ONLY_FALSE_SUBDOMAINS.has(subdomain)) {
    return false;
  }

  if (EXPLORER_ONLY_TRUE_SUBDOMAINS.has(subdomain)) {
    return true;
  }

  return null;
};

export const isExplorerOnlyMode = (): boolean => {
  const hostname = getHostname();

  const productDomainMode = getExplorerOnlyModeFromProductDomain(hostname);
  if (productDomainMode !== null) {
    return productDomainMode;
  }

  const subdomainMode = getExplorerOnlyModeFromSubdomain(hostname?.split(".")[0] ?? null);
  if (subdomainMode !== null) {
    return subdomainMode;
  }

  return env.EXPLORER_ONLY_MODE;
};
